import React, { useEffect, useState, useMemo } from 'react';
import { ENABLE_HOLIDAY_THEME, winterThemeConfig } from '../utils/winterThemeConfig';

const WinterEffects = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    const checkReducedMotion = () => {
      setPrefersReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    };

    checkMobile();
    checkReducedMotion();

    window.addEventListener('resize', checkMobile);
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    mediaQuery.addEventListener('change', checkReducedMotion);

    return () => {
      window.removeEventListener('resize', checkMobile);
      mediaQuery.removeEventListener('change', checkReducedMotion);
    };
  }, []);

  const confetti = useMemo(() => {
    if (!winterThemeConfig.confetti.enabled) return [];

    const count = isMobile
      ? winterThemeConfig.confetti.particleCount.mobile
      : winterThemeConfig.confetti.particleCount.desktop;

    const colors = ['#FFD700', '#FFA500', '#0099ff', '#87CEEB', '#FF6B6B', '#4ECDC4'];

    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      animationDuration: 3 + Math.random() * 4,
      animationDelay: Math.random() * 5,
      size: 6 + Math.random() * 8,
      opacity: 0.6 + Math.random() * 0.4,
      drift: -30 + Math.random() * 60,
      rotation: Math.random() * 360,
      color: colors[Math.floor(Math.random() * colors.length)],
      shape: Math.random() > 0.5 ? 'circle' : 'square',
    }));
  }, [isMobile]);

  const sparkles = useMemo(() => {
    if (!winterThemeConfig.sparkles.enabled) return [];

    const count = isMobile
      ? winterThemeConfig.sparkles.particleCount.mobile
      : winterThemeConfig.sparkles.particleCount.desktop;

    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      animationDuration: 2 + Math.random() * 3,
      animationDelay: Math.random() * 5,
      size: 1 + Math.random() * 2,
    }));
  }, [isMobile]);

  const fireworks = useMemo(() => {
    if (!winterThemeConfig.fireworks.enabled || isMobile) return [];

    return Array.from({ length: winterThemeConfig.fireworks.count }, (_, i) => ({
      id: i,
      left: 10 + (i * (80 / winterThemeConfig.fireworks.count)) + Math.random() * 10,
      top: 5 + Math.random() * 15,
      size: 40 + Math.random() * 60,
      animationDelay: Math.random() * 5,
      color: ['#FFD700', '#FFA500', '#0099ff', '#87CEEB'][Math.floor(Math.random() * 4)],
      burstDelay: Math.random() * 3,
    }));
  }, [isMobile]);

  if (!ENABLE_HOLIDAY_THEME) return null;
  if (prefersReducedMotion && winterThemeConfig.performance.respectReducedMotion) return null;

  return (
    <div className="winter-effects-container pointer-events-none fixed inset-0 z-40 overflow-hidden">
      {winterThemeConfig.confetti.enabled && (
        <div className="confetti-container absolute inset-0">
          {confetti.map((piece) => (
            <div
              key={piece.id}
              className="confetti absolute"
              style={{
                left: `${piece.left}%`,
                width: `${piece.size}px`,
                height: `${piece.size}px`,
                opacity: piece.opacity,
                animationDuration: `${piece.animationDuration}s`,
                animationDelay: `${piece.animationDelay}s`,
                backgroundColor: piece.color,
                '--drift': `${piece.drift}px`,
                '--rotation': `${piece.rotation}deg`,
                borderRadius: piece.shape === 'circle' ? '50%' : '0',
              } as React.CSSProperties}
            />
          ))}
        </div>
      )}

      {winterThemeConfig.sparkles.enabled && (
        <div className="sparkles-container absolute inset-0">
          {sparkles.map((sparkle) => (
            <div
              key={sparkle.id}
              className="sparkle absolute"
              style={{
                left: `${sparkle.left}%`,
                top: `${sparkle.top}%`,
                width: `${sparkle.size}px`,
                height: `${sparkle.size}px`,
                animationDuration: `${sparkle.animationDuration}s`,
                animationDelay: `${sparkle.animationDelay}s`,
              }}
            />
          ))}
        </div>
      )}

      {winterThemeConfig.frost.enabled && (
        <>
          <div
            className="frost-vignette-top absolute top-0 left-0 right-0 h-32 pointer-events-none"
            style={{ opacity: winterThemeConfig.frost.opacity }}
          />
          <div
            className="frost-vignette-bottom absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
            style={{ opacity: winterThemeConfig.frost.opacity }}
          />
          <div
            className="frost-corner-tl absolute top-0 left-0 w-48 h-48 pointer-events-none"
            style={{ opacity: winterThemeConfig.frost.opacity }}
          />
          <div
            className="frost-corner-tr absolute top-0 right-0 w-48 h-48 pointer-events-none"
            style={{ opacity: winterThemeConfig.frost.opacity }}
          />
        </>
      )}

      {winterThemeConfig.fireworks.enabled && !isMobile && (
        <div className="fireworks-container absolute inset-0 pointer-events-none">
          {fireworks.map((firework) => (
            <div
              key={firework.id}
              className="firework absolute"
              style={{
                left: `${firework.left}%`,
                top: `${firework.top}%`,
                width: `${firework.size}px`,
                height: `${firework.size}px`,
                animationDelay: `${firework.animationDelay}s`,
                '--firework-color': firework.color,
                '--burst-delay': `${firework.burstDelay}s`,
              } as React.CSSProperties}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <div
                  key={i}
                  className="firework-particle absolute"
                  style={{
                    '--particle-angle': `${i * 30}deg`,
                    '--particle-color': firework.color,
                  } as React.CSSProperties}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WinterEffects;
